import { Alert, DateRange, useLocale, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { useAnalytics } from 'hooks/analytics/useAnalytics'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { isUndefinedOrNull } from 'util/checks'

import { Aggregator, AggregatorEnum } from './components/aggregator/model'
import { aggregatorByIdOrDefault } from './components/aggregator/utils'
import { BoardField, FieldFiltersByKey } from './components/board/model'
import { PivotTableBoard } from './components/board/PivotTableBoard'
import { PivotTableProvider } from './components/cell/PivotTableProvider'
import { PivotTableGrid } from './components/grid/PivotTableGrid'
import { KeyMap } from './components/model'
import { Dictionary, TreeMeta } from './tree-builder/model-treeBuilder'
import { noKeyErrorText } from './util-pivotTable'

export type PivotTableProps<T extends object> = {
  title: string
  keyMapping: KeyMap<T>
  treeMeta: TreeMeta<T>
  maxLeafValue: number
  defaultTree: Dictionary<T>
  complementaryTree: Dictionary<T>
  isBuilding: boolean
  build<K extends keyof T>(
    rowKeys: Array<K>,
    columnKeys: Array<K>,
    aggregator: Aggregator,
    aggregatorKey: keyof T,
    filterKeys?: Map<K, Set<string>>
  )
  demolish: () => void
  dateRangeFilter?: DateRange | null
  initialValues?: PivotTableInitialValues<T>
  enableSearchField?: boolean
  groupByLabel?: string
  showTotals?: boolean
}

export interface PivotTableInitialValues<T> {
  fields: ReadonlyArray<BoardField<T>>
  aggregatorId: AggregatorEnum
}

export function PivotTable<T extends object>(props: Readonly<PivotTableProps<T>>) {
  const {
    keyMapping,
    title,
    treeMeta,
    maxLeafValue,
    defaultTree,
    complementaryTree,
    isBuilding,
    build,
    demolish,
    dateRangeFilter,
    initialValues,
    enableSearchField = false,
    groupByLabel,
    showTotals = true,
  } = props

  const locale = useLocale()

  const keyValues = treeMeta?.keyValues
  const numberKeys = treeMeta?.numberKeys
  const initialAggregator = aggregatorByIdOrDefault(initialValues?.aggregatorId, locale.aggregators)

  const pivotTableRef = useRef(null)

  const [filterDataKeyValues, setFilterDataKeyValue] = useState<FieldFiltersByKey<T>>()
  const [rowKeys, setRowKeys] = useState<Array<keyof T>>([])
  const [columnKeys, setColumnKeys] = useState<Array<keyof T>>([])

  const [aggregator, setAggregator] = useState<Aggregator>(initialAggregator)
  const [aggregatorKey, setAggregatorKey] = useState<keyof T>()

  const { analytics } = useAnalytics()
  const alert = useAlert()

  const handleAggregatorKey = (key: keyof T) => setAggregatorKey(key)
  const handleAggregator = (aggregator: Aggregator) => {
    setAggregator(() => aggregator)
  }

  const hasModeloSelecionado = !isUndefinedOrNull(initialValues)

  useEffect(() => {
    if (rowKeys?.length > 0 || columnKeys?.length > 0) {
      const init = performance.now()

      build(rowKeys, columnKeys, aggregator, aggregatorKey, filterDataKeyValues)

      const final = performance.now()

      const format = 'DD/MM/YYYY'

      analytics.logEvent('relatorios_bi_gerar_tabela', {
        relatorio: title,
        periodo: dateRangeFilter
          ? `${moment(dateRangeFilter.startDate).format(format)} até ${moment(dateRangeFilter.endDate).format(format)}`
          : undefined,
        tempo: `${(final - init).toFixed(2)} ms`,
        linhas: rowKeys.map((r) => keyMapping.get(r).keyName).join(', '),
        colunas: columnKeys.map((c) => keyMapping.get(c).keyName).join(', '),
      })
    }
  }, [
    rowKeys,
    columnKeys,
    filterDataKeyValues,
    aggregator,
    aggregatorKey,
    build,
    analytics,
    title,
    keyMapping,
    dateRangeFilter,
  ])

  useEffect(() => {
    if (defaultTree) {
      if (defaultTree.nodeValue === undefined) {
        alert('warning', 'Os filtros selecionados ou a combinação de linhas e colunas não possuem dados')
      } else if ((rowKeys?.length > 0 || columnKeys?.length) && !isBuilding) {
        window.scrollTo({ behavior: 'smooth', top: pivotTableRef.current?.offsetTop })
      }
    }
  }, [alert, columnKeys, defaultTree, isBuilding, rowKeys])

  const handleSubmit = (values: [Array<keyof T>, Array<keyof T>], newFilters: FieldFiltersByKey<T>) => {
    const [newRowKeys, newColumnKeys] = values

    if (newRowKeys.length === 0 && newColumnKeys.length === 0) {
      alert('warning', 'Arraste pelo menos um dos campos disponíveis para os quadros de linhas ou colunas.')
      return
    }

    const errorList = []

    for (const rowKey of newRowKeys) {
      const isFilterEmpty = !newFilters?.get(rowKey)?.size
      isFilterEmpty && errorList.push(keyMapping.get(rowKey).keyName)
    }

    for (const columnKey of newColumnKeys) {
      const isFilterEmpty = !newFilters?.get(columnKey)?.size
      isFilterEmpty && errorList.push(keyMapping.get(columnKey).keyName)
    }

    if (errorList.length) {
      alert(
        'warning',
        'Nenhuma opção selecionada para o(s) campo(s) ' +
          noKeyErrorText(errorList) +
          'presente(s) no(s) quadros de linhas e colunas.'
      )
      return
    }

    setFilterDataKeyValue(newFilters)
    setRowKeys(newRowKeys)
    setColumnKeys(newColumnKeys)

    if (newRowKeys !== rowKeys || newColumnKeys !== columnKeys || newFilters !== filterDataKeyValues) {
      demolish()
    }

    analytics.logEvent('pivot_table_build', {
      hasModeloSelecionado,
    })
  }

  const handleReset = () => {
    demolish()
    setRowKeys([])
    setColumnKeys([])
  }

  if (keyValues) {
    const filteredInitialValueFields = initialValues?.fields.filter((value) => keyValues.has(value.key))
    return (
      <VFlow>
        <Alert inline type='info'>
          Para gerar uma tabela, arraste os <b>Campos Disponíveis</b> desejados para os quadros de <b>Linhas</b> e{' '}
          <b>Colunas</b>, selecione a forma de apresentação e clique em <b>Gerar tabela</b>.
        </Alert>
        <PivotTableBoard<T>
          keys={keyValues}
          keyMapping={keyMapping}
          onSubmit={handleSubmit}
          onReset={handleReset}
          numberKeys={numberKeys}
          isBuilding={isBuilding}
          aggregator={{
            onChange: handleAggregator,
            onKeyChange: handleAggregatorKey,
            value: aggregator,
            key: aggregatorKey,
          }}
          initialFields={filteredInitialValueFields}
          enableSearchField={enableSearchField}
        />
        {defaultTree &&
          defaultTree.nodeValue !== undefined &&
          !isBuilding &&
          (rowKeys?.length > 0 || columnKeys?.length > 0) && (
            <PivotTableProvider maxValue={maxLeafValue} suffix={aggregator?.suffix}>
              <div ref={pivotTableRef}>
                <PivotTableGrid
                  rowKeys={rowKeys}
                  columnKeys={columnKeys}
                  keysMapping={keyMapping}
                  defaultTree={defaultTree}
                  complementaryTree={complementaryTree}
                  title={title}
                  filterDataKeyValues={filterDataKeyValues ?? new Map()}
                  dateRangeFilter={dateRangeFilter}
                  aggregatorLabel={aggregator.label}
                  groupByLabel={groupByLabel}
                  showTotals={showTotals}
                />
              </div>
            </PivotTableProvider>
          )}
      </VFlow>
    )
  } else {
    return (
      <div>
        <b>Loading...</b>
      </div>
    )
  }
}
